import { useState } from 'react';

import produce from 'immer';
import create from 'zustand';

import { TableStore } from '@components/table/table-store';
import { ImmerUpdateFn } from '@utils/types';

import { ExtraStateStore } from './extra-state-store';

type TravellersColumnControlStoreState = {
  isOpen: boolean;
  selectedActionId: string;
  focusedVariableId: string;
};

export const createTravellersColumnControlStore = (
  tableStore: TableStore,
  extraStateStore: ExtraStateStore,
  onFieldsNeeded?: (newFieldIds: string[]) => void
) => {
  const useStore = create<TravellersColumnControlStoreState>(() => ({
    isOpen: false,
    selectedActionId: '',
    focusedVariableId: '',
  }));

  const update = (fn: ImmerUpdateFn<TravellersColumnControlStoreState>) => {
    return useStore.setState((state) => produce(state, fn));
  };

  const setOpenness = (value: boolean) => {
    update((state) => {
      state.isOpen = value;
    });

    if (!value) {
      unselectAction();
    }
  };

  const close = () => {
    setOpenness(false);
  };

  const open = () => {
    setOpenness(true);
  };

  const toggleColumn = (id: string, value: boolean) => {
    if (value) {
      tableStore.actions.addColumn(id);
      extraStateStore.actions.addActionId(id, onFieldsNeeded);
    } else {
      tableStore.actions.removeColumn(id);
      extraStateStore.actions.removeActionId(id);
    }
  };

  const focusColumn = (columnId: string) => {
    const actionId = columnId.split('__')[1];
    update((state) => {
      state.isOpen = true;
      state.selectedActionId = actionId;
      state.focusedVariableId = columnId;
    });

    setTimeout(() => {
      update((state) => {
        state.focusedVariableId = '';
      });
    }, 100);
  };

  const selectAction = (actionId: string) => {
    update((state) => {
      state.selectedActionId = actionId;
    });
  };

  const unselectAction = () => {
    update((state) => {
      state.selectedActionId = '';
    });
  };

  const useIsOpen = () => useStore((state) => state.isOpen);
  const useFocusedVariableId = () => useStore((state) => state.focusedVariableId);
  const useSelectedActionId = () => useStore((state) => state.selectedActionId);

  return {
    useIsOpen,
    useFocusedVariableId,
    useSelectedActionId,
    actions: {
      getState: useStore.getState,
      setOpenness,
      toggleColumn,
      focusColumn,
      selectAction,
      unselectAction,
      close,
      open,
    },
  };
};

export const useTravellersColumnControlStore = (
  tableStore: TableStore,
  extraStateStore: ExtraStateStore,
  onFieldsNeeded?: (newFieldIds: string[]) => void
) => {
  return useState(() =>
    createTravellersColumnControlStore(tableStore, extraStateStore, onFieldsNeeded)
  )[0];
};

export type TravellersColumnControlStore = ReturnType<typeof useTravellersColumnControlStore>;

export type TravellersColumnControlActions = TravellersColumnControlStore['actions'];
